// Words and numbers for the admin console (B197): sizes, how long ago, the dial's modes, and
// the lines a connector says. Pure, so format.test.ts can hold them to their wording.
import type { ConnectorView } from './api.js';

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

/** A size as a person reads it: bytes up to a kilobyte, then KB, MB and GB to one decimal. */
export function formatBytes(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs < KB) return `${sign}${abs} B`;
  if (abs < MB) return `${sign}${(abs / KB).toFixed(1)} KB`;
  if (abs < GB) return `${sign}${(abs / MB).toFixed(1)} MB`;
  return `${sign}${(abs / GB).toFixed(1)} GB`;
}

/** A count with thousands separators. */
export const n = (x: number): string => x.toLocaleString('en-GB');

/** How long ago, in the largest whole unit: "just now", "4 min ago", "3 h ago", "2 days ago". */
export function ago(isoTime: string | null, now: number = Date.now()): string {
  if (!isoTime) return '—';
  const s = Math.max(0, Math.round((now - Date.parse(isoTime)) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86_400);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

export const MODE_LABELS: Record<string, string> = {
  master_only: 'Master only',
  shadow: 'Shadow',
  apprentice_gated: 'Apprentice, gated',
  apprentice_only: 'Apprentice only',
};

/** The backfill cell: done with its count, in progress with its count of the expected, or not begun. */
export function backfillLine(c: ConnectorView): string {
  const b = c.backfill;
  if (!b) return c.tenantLevel ? '—' : 'not started';
  if (b.completedAt) return `done ${b.completedAt.slice(0, 10)} · ${n(b.done)}`;
  if (b.failedAt) return `stopped at ${n(b.done)}${b.expected !== null ? ` of ${n(b.expected)}` : ''}`;
  if (b.startedAt) return `${n(b.done)}${b.expected !== null ? ` of ${n(b.expected)}` : ''} so far`;
  return 'not started';
}

/**
 * BUGS.md X27: how long a connector that calls itself ready or connected has gone without a pull, in whole days, when
 * that is longer than `IDLE_AFTER_DAYS`; null when it pulled more recently, has never pulled (the Last pull cell says
 * "no pull yet"), or is in any other state, which says itself. The stored status is what the connector last set, and a
 * connector whose job was switched off (Fathom's, 24 Sep) or that nothing runs any more (Motion's) never sets another:
 * it read "ready", with the green dot, weeks after its last pull. Two days: the slowest connector that pulls on a timer
 * pulls nightly, so a missed night is one day and two is a stop.
 */
export const IDLE_AFTER_DAYS = 2;
export function idleDays(c: ConnectorView, now: number = Date.now()): number | null {
  if ((c.status !== 'ready' && c.status !== 'connected') || !c.lastPull) return null;
  const days = Math.floor((now - Date.parse(c.lastPull.startedAt)) / 86_400_000);
  return days >= IDLE_AFTER_DAYS ? days : null;
}

/** Whether the status dot may be green: ready or connected, and pulling (never pulled, or idle, is not). */
export function pulling(c: ConnectorView, now: number = Date.now()): boolean {
  return (c.status === 'ready' || c.status === 'connected') && c.lastPull !== null && idleDays(c, now) === null;
}

/** What the connector says: its plain failure message, the admin alert, and D98's parking, how long it has gone without a pull, or nothing. */
export function saysLine(c: ConnectorView, now: number = Date.now()): string {
  if (c.status === 'failed') {
    const parts = [`"${c.failureMessage ?? 'failed with no message'}"`];
    if (c.adminAlertedAt) parts.push(`admin alerted ${ago(c.adminAlertedAt, now)}`);
    if (c.parksItsUser) parts.push(`${c.who ?? 'its person'} waits at the loading screen until this is put back, because it is mandatory`);
    return parts.join(' · ');
  }
  if (c.status === 'pending') return 'waiting to be connected';
  if (c.status === 'disconnected') return 'disconnected';
  const idle = idleDays(c, now);
  const parts = idle !== null ? [`no pull for ${idle} days`] : [];
  if (c.lastPull?.error) parts.push(`last run said "${c.lastPull.error}"`);
  return parts.join(' · ');
}

/** The storage growth line: per week, and across how many days it was measured when that is not seven. */
export function growthLine(bytesPerWeek: number, spanDays: number): string {
  const sign = bytesPerWeek > 0 ? '+' : '';
  const span = spanDays === 7 ? 'a week' : `a week, measured over ${spanDays} ${spanDays === 1 ? 'day' : 'days'}`;
  return `${sign}${formatBytes(bytesPerWeek)} ${span}`;
}
